import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip, Typography,
  Table, TableHead, TableBody, TableRow, TableCell, Alert,
} from '@mui/material';
import { colorForOutcome, formatForwardSummary, countUndrawable } from '../utils/spiralInspectOverlays';

/**
 * The inspection result: every rung of the ladder, with what it found.
 *
 * <p>A table rather than a tooltip because the numbers are the substance. A convergence is only
 * checkable if a reader can see which turn, at which Fibonacci step, across how many days, and how
 * far off it landed - and a hover panel is the wrong place to read twenty rows of that.
 *
 * <h2>Two things this is careful not to overstate</h2>
 *
 * The hit rate names its denominator. Rungs still in the future are shown, but excluded from the
 * fraction, because a ladder cast from a recent turn has most of its long rungs ahead of it and
 * counting those as failures would rate an anchor on how much time has passed rather than on
 * whether it was right.
 *
 * <p>Rungs that fall past the last loaded bar are counted in words. They cannot be drawn, and a
 * chart showing three lines when the ladder has nine is a quieter kind of wrong than a chart
 * showing none.
 */
export default function SpiralInspectDialog({ open, inspection, symbol, onClose, onInspectBar }) {
  if (!inspection) return null;

  const isForward = inspection.mode !== 'BACKWARD';
  const params = inspection.params || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isForward ? '🔮 Φ Projection' : '📜 Φ Lineage'}
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {symbol}
          {isForward
            ? ` · from ${inspection.anchor?.date ?? `bar ${inspection.barIndex}`}`
            : ` · onto ${inspection.target?.date ?? `bar ${inspection.barIndex}`}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip size="small" label={`${params.periodName || 'synodic'} month · ${round(params.periodDays)}d`} />
          <Chip size="small" label={`minWindow ${params.minWindow}`} />
          <Chip size="small" label={`tolerance ±${params.tolerance} bars`} />
          {isForward && inspection.anchor && (
            <Chip
              size="small"
              color={inspection.anchor.isPivot ? 'success' : 'default'}
              label={inspection.anchor.isPivot
                ? `anchor is a turn · N=${inspection.anchor.pivot?.windowN}`
                : 'anchor is not a detected turn'}
            />
          )}
        </Box>

        {/* Said plainly. A ladder cast from an ordinary bar is a different kind of claim from one
            cast from a confirmed turn, and nothing else on screen distinguishes them. */}
        {isForward && inspection.anchor && !inspection.anchor.isPivot && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This bar is not a detected pivot at minWindow {params.minWindow}. Its ladder is still
            projected, but a spiral cast from a bar that is not a turn is not evidence of the same
            kind as one cast from a confirmed high or low.
          </Alert>
        )}

        {isForward ? forwardBody(inspection, onInspectBar) : backwardBody(inspection, onInspectBar)}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function forwardBody(inspection, onInspectBar) {
  const rungs = inspection.projections || [];
  const summary = inspection.summary || {};
  const undrawable = countUndrawable(inspection, Array.from({ length: inspection.barCount || 0 }));

  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {formatForwardSummary(inspection) || 'No rungs reached this chart.'}
        {summary.hitRate != null && ` · hit rate ${Math.round(summary.hitRate * 100)}%`}
      </Typography>

      {undrawable > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.8 }}>
          {undrawable} rung{undrawable === 1 ? '' : 's'} land past the last loaded bar and are listed
          here but not drawn.
        </Typography>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>F(n)</TableCell>
            <TableCell align="right">Delay</TableCell>
            <TableCell>Target date</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Turn found</TableCell>
            <TableCell align="right">Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rungs.map((r) => (
            <TableRow key={r.fibNumber} hover>
              <TableCell sx={{ fontFamily: 'monospace' }}>F({r.fibNumber})</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{Math.round(r.delayDays)}d</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>
                {r.targetDate}
                {/* An extrapolated date is a different claim from a located one. */}
                {r.estimated && <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}> ~est</Typography>}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={r.outcome === 'VERIFIED' ? 'hit' : r.outcome === 'FUTURE' ? 'ahead' : 'no turn'}
                  sx={{ bgcolor: colorForOutcome(r.outcome), color: '#fff', fontWeight: 600 }}
                />
              </TableCell>
              <TableCell>
                {r.matchedPivot ? (
                  <Button
                    size="small"
                    sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                    onClick={() => onInspectBar?.(r.matchedPivot.barIndex)}
                  >
                    {r.matchedPivot.type} N={r.matchedPivot.windowN}
                  </Button>
                ) : '—'}
              </TableCell>
              {/* Signed. A set of consistently positive errors is a systematic lag, which an
                  absolute value would hide behind an average that looks like accuracy. */}
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                {r.deltaDays == null ? '—' : `${r.deltaDays > 0 ? '+' : ''}${r.deltaDays.toFixed(1)}d`}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function backwardBody(inspection, onInspectBar) {
  const parents = inspection.parents || [];

  if (parents.length === 0) {
    return (
      <Alert severity="info">
        No earlier turn projects onto this bar at ±{inspection.params?.tolerance} bars. That is a
        real answer, not a failure: most bars are not spiral targets.
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {inspection.convergenceCount} distinct turn{inspection.convergenceCount === 1 ? '' : 's'} point
        here · score {Math.round(inspection.totalWeightScore ?? 0)}
        {inspection.target?.isFuture && ' · this bar is beyond the loaded series'}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Parent turn</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Strength</TableCell>
            <TableCell>F(n)</TableCell>
            <TableCell align="right">Delay</TableCell>
            <TableCell align="right">Landed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parents.map((p) => (
            <TableRow key={`${p.pivotBarIndex}-${p.fibNumber}`} hover>
              <TableCell>
                <Button
                  size="small"
                  sx={{ textTransform: 'none', p: 0, minWidth: 0, fontFamily: 'monospace' }}
                  onClick={() => onInspectBar?.(p.pivotBarIndex)}
                >
                  {p.pivotDate}
                </Button>
              </TableCell>
              <TableCell>{p.pivotType}</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>N={p.pivotWindowN}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>F({p.fibNumber})</TableCell>
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{Math.round(p.delayDays)}d</TableCell>
              {/* Bars and days both: two parents can hit the same bar with very different
                  precision, and "same bar" alone conceals that. */}
              <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                {p.deltaBars === 0 ? 'on bar' : `${p.deltaBars > 0 ? '+' : ''}${p.deltaBars} bars`}
                {/* Omitted rather than shown as 0.0 when the landing bar is past the series: there
                    is no bar there to measure against, and a zero would read as a perfect hit. */}
                {p.driftDays != null && (
                  <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}>
                    {' '}({p.driftDays > 0 ? '+' : ''}{Number(p.driftDays).toFixed(1)}d)
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function round(v) {
  return typeof v === 'number' ? Math.round(v * 100) / 100 : v;
}

SpiralInspectDialog.propTypes = {
  open: PropTypes.bool,
  /** The /api/spiral/inspect response. Null renders nothing. */
  inspection: PropTypes.object,
  symbol: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** Re-inspect another bar from inside the dialog, for walking a lineage back. */
  onInspectBar: PropTypes.func,
};
